import { getFullName } from '@provision/formatting-utils';
import { useFragment, useMutation } from '@provision/graphql-client';
import { useCallback, useMemo, useRef } from 'react';
import { groupReactions, ReactionSummary } from 'src/components/Reactions';
import { isTempMessageId } from 'src/graphql/entities/conversation/temp-message-id';
import {
  removeReactionFromCache,
  upsertReactionInCache,
} from 'src/graphql/entities/conversation-message-reaction/conversation-message-reaction.cache';
import {
  ADD_CONVERSATION_MESSAGE_REACTION_MUTATION,
  MESSAGE_REACTIONS_FRAGMENT,
  REMOVE_CONVERSATION_MESSAGE_REACTION_MUTATION,
} from 'src/graphql/entities/conversation-message-reaction/conversation-message-reaction.operations';
import {
  AddConversationMessageReactionResult,
  ConversationMessageReactionVars,
  MessageReactionsData,
  RemoveConversationMessageReactionResult,
} from 'src/graphql/entities/conversation-message-reaction/conversation-message-reaction.types';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { useSnackbar } from 'src/utils/hooks/use-snackbar';

const UNKNOWN_REACTOR_NAME = 'Unknown';

export interface MessageReactions {
  reactions: ReactionSummary[];
  /** False until the message has a server id; the server has no row to attach a reaction to. */
  isReactable: boolean;
  addReaction: (emoji: string) => void;
  toggleReaction: (emoji: string, reactedByMe: boolean) => void;
}

/**
 * A message's reactions as chips, read from its cached `ConversationMessage`, with optimistic
 * add/remove for the current user.
 */
export function useMessageReactions(conversationMessageId: string): MessageReactions {
  const { user } = useCurrentUserProfile();
  const { showErrorAlert } = useSnackbar();

  const fragment = useFragment<MessageReactionsData>({
    fragment: MESSAGE_REACTIONS_FRAGMENT,
    fragmentName: 'MessageReactions',
    from: { __typename: 'ConversationMessage', id: conversationMessageId },
  });
  // A message cached without its reactions (e.g. not from the timeline query) shows none.
  const data = fragment.complete ? fragment.data : undefined;

  const records = data?.reactions;
  const reactions = useMemo(() => {
    const names = new Map(
      (records ?? []).map(({ userId, platformUser }) => [userId, getFullName(platformUser)]),
    );
    return groupReactions(
      records ?? [],
      user?.userId,
      (userId) => names.get(userId) || UNKNOWN_REACTOR_NAME,
    );
  }, [records, user?.userId]);

  const [addMutation] = useMutation<
    AddConversationMessageReactionResult,
    ConversationMessageReactionVars
  >(ADD_CONVERSATION_MESSAGE_REACTION_MUTATION, {
    update: (cache, { data: result }) => {
      if (result) {
        upsertReactionInCache(cache, result.addConversationMessageReaction);
      }
    },
  });

  const [removeMutation] = useMutation<
    RemoveConversationMessageReactionResult,
    ConversationMessageReactionVars
  >(REMOVE_CONVERSATION_MESSAGE_REACTION_MUTATION);

  const conversationId = data?.conversationId;
  const partnerId = data?.partnerId;
  const userId = user?.userId;
  const isReactable = !isTempMessageId(conversationMessageId);

  // Taps on an emoji are dropped while its add/remove is in flight, so responses can't land out
  // of order and restore a reaction the viewer just took off.
  const pendingEmojis = useRef(new Set<string>());
  const runExclusive = useCallback(
    (emoji: string, run: () => Promise<unknown>, errorMessage: string) => {
      if (pendingEmojis.current.has(emoji)) {
        return;
      }
      pendingEmojis.current.add(emoji);
      run()
        .catch(() => showErrorAlert(errorMessage))
        .finally(() => pendingEmojis.current.delete(emoji));
    },
    [showErrorAlert],
  );

  const addReaction = useCallback(
    (emoji: string) => {
      if (!isReactable || !userId || !conversationId || partnerId === undefined) {
        return;
      }
      const optimisticId = `optimistic:${conversationMessageId}:${userId}:${emoji}`;
      runExclusive(
        emoji,
        () =>
          addMutation({
            variables: { params: { conversationMessageId, emoji } },
            optimisticResponse: {
              addConversationMessageReaction: {
                __typename: 'ConversationMessageReaction',
                id: optimisticId,
                conversationMessageReactionId: optimisticId,
                conversationMessageId,
                conversationId,
                partnerId,
                userId,
                emoji,
                createdAt: new Date().toISOString(),
                platformUser: {
                  __typename: 'PlatformUser',
                  id: userId,
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                },
              },
            },
          }),
        'Could not add the reaction. Please try again.',
      );
    },
    [
      addMutation,
      conversationId,
      conversationMessageId,
      isReactable,
      partnerId,
      runExclusive,
      user?.firstName,
      user?.lastName,
      userId,
    ],
  );

  const removeReaction = useCallback(
    (emoji: string) => {
      if (!isReactable || !userId) {
        return;
      }
      runExclusive(
        emoji,
        () =>
          removeMutation({
            variables: { params: { conversationMessageId, emoji } },
            optimisticResponse: { removeConversationMessageReaction: { removed: true } },
            update: (cache) =>
              removeReactionFromCache(cache, { conversationMessageId, userId, emoji }),
          }),
        'Could not remove the reaction. Please try again.',
      );
    },
    [conversationMessageId, isReactable, removeMutation, runExclusive, userId],
  );

  const toggleReaction = useCallback(
    (emoji: string, reactedByMe: boolean) =>
      reactedByMe ? removeReaction(emoji) : addReaction(emoji),
    [addReaction, removeReaction],
  );

  return { reactions, isReactable, addReaction, toggleReaction };
}
